'use strict';

import { doFetch } from '../../utils/CommonUtils';
import Routes from '../../utils/Routes';
import FluxEventEmitter from '../FluxEventEmitter';
import assign from 'object-assign';
import Constants from '../Constants';

const ChangeEvents = {
	Voting: 'voting',
	ContestHead: 'contesthead',
	ContestGrid: 'contestGrid',
	ShouldOpenMedia: 'ShouldOpenMedia',
	OpenAvsModal: 'OpenAvsModal',
	GuestData: 'guestData',
};

const contestGridData = {};
const contestVotings = [];
const contestHeaderData = [];
let contestShouldOpenMedia = false;
let contestShouldOpenMediaConfig = null;

function arrayConcatUnique(arr1, arr2) {
	const newArray = [...arr1];

	for (let i = 0; i < arr2.length; i++) {
		const el = arr2[i];

		const foundIndex = arr1.findIndex((element) => el === element || (el.itemHash && element.itemHash && el.itemHash === element.itemHash));

		if (foundIndex === -1) {
			newArray.push(el);
		}
	}

	return newArray;
}

const ContestStore = assign({}, FluxEventEmitter.prototype, {
	EVENTS: ChangeEvents,

	init: function (contestsGridConfig, contestId, contestArea, guestData) {
		if (contestsGridConfig && contestId && contestArea && guestData) {
			contestGridData[contestId] = {};
			contestGridData[contestId][contestArea] = { contestsGridConfig, id: contestId };
			contestGridData[contestId].guestData = guestData;
			ContestStore.emit(ChangeEvents.ContestGrid, contestId, contestArea);
			ContestStore.emit(ChangeEvents.GuestData, guestData);
		}
	},

	getContestVoting: function (contestId) {
		return contestVotings[contestId];
	},

	getContestHeaderData: function (contestId) {
		return contestHeaderData[contestId];
	},

	getContestGridConfig: function (contestId, contestArea) {
		if (!contestGridData[contestId]) {
			contestGridData[contestId] = {};
		}
		return contestGridData[contestId][contestArea];
	},

	getShouldOpenMedia: function () {
		return contestShouldOpenMedia;
	},

	getShouldOpenMediaConfig: function () {
		return contestShouldOpenMediaConfig;
	},

	getGuestData(contestId) {
		return contestGridData[contestId]?.guestData;
	},

	// Direct methods (previously handled via actions)
	addContestMediaVoting: function (contestId, mediaId, actorId, guestId, albumId, contestName, hideConfirmWindow) {
		doFetch(
			Routes.getRoute(Routes.Names.CONTESTS_ADD),
			{
				actorId: actorId,
				contestId: contestId,
				guestId: guestId,
				mediaId: mediaId,
				albumId: albumId,
				hideConfirmWindow: hideConfirmWindow,
			},
			Constants.HttpMethods.POST,
			true,
		).then((data) => {
			contestVotings[data.mediaId] = data.mediaVotes;

			if (data.success) {
				contestGridData[contestName].guestData.voteCount++;
				contestGridData[contestName].guestData.hideVoteLimitModal = data.hideVoteLimitModal;
				ContestStore.emit(ChangeEvents.GuestData, contestGridData[contestName].guestData);
			}

			if (data.needAvs) {
				ContestStore.emit(ChangeEvents.OpenAvsModal, true);
			} else if (data.success) {
				ContestStore.emit(ChangeEvents.Voting, data.guestId, data.contestId, data.mediaId, true);
			}
		});
	},

	loadContestHead: function (contestId, language) {
		if (typeof contestHeaderData[contestId] !== 'undefined') {
			ContestStore.emit(ChangeEvents.ContestHead, contestId);
		} else {
			doFetch(
				Routes.getRoute(Routes.Names.CONTESTS_GET_CONTEST_HEAD, {
					contestId: contestId,
					language: language,
				}),
				{},
				Constants.HttpMethods.GET,
				true,
			).then((data) => {
				contestHeaderData[contestId] = data.data;
				ContestStore.emit(ChangeEvents.ContestHead, contestId);
			});
		}
	},

	loadContestGridItems: function (contestId, contestArea, limit, offset, useCache, actorName, mediaIndex) {
		const cachedItems = contestGridData[contestId]?.[contestArea]?.contestsGridConfig?.contestGridItems;
		const hasCachedPage =
			Array.isArray(cachedItems) &&
			(cachedItems.length >= limit + offset || (offset === 0 && cachedItems.length === 0));

		if (useCache && hasCachedPage) {
			ContestStore.emit(ChangeEvents.ContestGrid, contestId, contestArea);
		} else {
			doFetch(
				Routes.getRoute(Routes.Names.CONTESTS_GET_CONTEST_GRID_ITEMS, {
					contestId: contestId,
					limit: limit,
					contestSub: contestArea,
					offset: offset,
				}),
				{ actorName: actorName, mediaIndex: mediaIndex },
				Constants.HttpMethods.GET,
				true,
			).then((data) => {
				if (!contestGridData[contestId]) {
					contestGridData[contestId] = {};
				}
				if (!contestGridData[contestId][contestArea]) {
					contestGridData[contestId][contestArea] = {};
				}
				if (!contestGridData[contestId][contestArea].contestsGridConfig) {
					contestGridData[contestId][contestArea].contestsGridConfig = {};
				}

				let oldItems = [];

				if (contestGridData[contestId][contestArea].contestsGridConfig.contestGridItems) {
					oldItems = contestGridData[contestId][contestArea].contestsGridConfig.contestGridItems;
				}

				contestGridData[contestId][contestArea].contestsGridConfig.contestGridItems = arrayConcatUnique(
					oldItems,
					data.data.contestsGridItems || [],
				);
				contestGridData[contestId][contestArea].contestsGridConfig.elementsCount = data.data.elementsCount;
				contestGridData[contestId][contestArea].contestsGridConfig.guestId = data.data.guestId;
				contestGridData[contestId][contestArea].id = data.data.id;
				contestGridData[contestId].guestData = data.data.guestData;

				ContestStore.emit(ChangeEvents.ContestGrid, contestId, contestArea);
				ContestStore.emit(ChangeEvents.GuestData, contestGridData[contestId].guestData);
			});
		}
	},

	setShouldOpenMedia: function (shouldOpen, config) {
		contestShouldOpenMedia = shouldOpen;
		contestShouldOpenMediaConfig = config;

		ContestStore.emit(ChangeEvents.ShouldOpenMedia, shouldOpen, config);
	},

	loadGuestData: function (contestId, contestArea) {
		doFetch(
			Routes.getRoute(Routes.Names.CONTESTS_GET_GUEST_DATA, {
				contestId: contestId,
			}),
			{ contestId: contestId },
			Constants.HttpMethods.GET,
			true,
		).then((data) => {
			if (!contestGridData[contestId]) {
				contestGridData[contestId] = {};
			}
			if (!contestGridData[contestId][contestArea]) {
				contestGridData[contestId][contestArea] = {};
			}

			contestGridData[contestId].guestData = data.data;

			ContestStore.emit(ChangeEvents.GuestData, contestGridData[contestId].guestData);
		});
	},
});

export default ContestStore;
